// Shared email-attachment numbers + pure helpers.
//
// ONE honest pair of limits for the whole mail stack (composer, draft store,
// server): 20 MiB per file and 20 MiB total decoded bytes per message
// (Exchange Online's 35 MB ceiling applies to the BASE64 size, so 20 MiB raw
// leaves headroom). Before this file the composer promised 10 MB/25 MB, the
// send endpoint silently enforced ~3 MB, and draft autosave broke near
// 0.75 MB (Firestore's 1 MiB document cap) - three different walls, one shown.
//
// Storage model (contract B): small files stay inline base64 in the composer
// state / draft doc; anything bigger is uploaded to Cloud Storage
// (mail_attachments/{uid}/{draftKey}/...) and only a reference travels in
// drafts and send payloads.
//
// Everything here is pure (no Firebase / network) so it can be unit-tested.

export const MIB = 1024 * 1024;

/** Per-file ceiling, enforced at pick time and again by the server. */
export const MAIL_ATTACH_MAX_FILE_BYTES = 20 * MIB;
/** Total decoded attachment bytes per message. */
export const MAIL_ATTACH_MAX_TOTAL_BYTES = 20 * MIB;

/** A file at or under this stays inline base64 (invoice PDFs are ~40 KB). */
export const INLINE_MAX_FILE_BYTES = 150 * 1024;
/** Total inline base64 across one message's attachments. */
export const INLINE_MAX_TOTAL_BYTES = 400 * 1024;

/** Serialised-attachments budget for one email_drafts doc (Firestore caps a
 *  doc at 1 MiB; the body + textBody need the rest). */
export const DRAFT_ATTACHMENTS_MAX_BYTES = 700 * 1024;

/** The classic composer has no Storage upload - it posts base64 in the JSON
 *  body, and a Cloud Functions v1 HTTP request is capped at 10 MB on the wire
 *  (base64 inflates 4/3). 6 MiB raw keeps it safely under that. */
export const CLASSIC_INLINE_MAX_TOTAL_BYTES = 6 * MIB;

/** Forward: cid: images are re-embedded as data: URLs in the editor, so they
 *  get a small budget (they end up inside the draft body too). */
export const FORWARD_IMAGE_MAX_COUNT = 8;
export const FORWARD_IMAGE_MAX_BYTES = 400 * 1024;

// -- Formatting --------------------------------------------------------------

/** "512 B" / "41.3 KB" / "3.2 MB". */
export function formatBytes(n: number): string {
  const v = Number.isFinite(n) && n > 0 ? n : 0;
  if (v < 1024) return `${Math.round(v)} B`;
  const kb = v / 1024;
  if (kb < 1023.95) return `${kb.toFixed(1)} KB`;
  return `${(v / MIB).toFixed(1)} MB`;
}

/** A limit as the user reads it: 20 MiB -> "20 MB", 6 MiB -> "6 MB". */
export function formatLimit(bytes: number): string {
  return `${Number((bytes / MIB).toFixed(1))} MB`;
}

/** "3.2 MB of 20 MB" - the live tray indicator. */
export function formatTotalIndicator(usedBytes: number, maxBytes: number = MAIL_ATTACH_MAX_TOTAL_BYTES): string {
  return `${formatBytes(usedBytes)} of ${formatLimit(maxBytes)}`;
}

// -- Pick-time validation ----------------------------------------------------

export type CanAddResult =
  | { ok: true }
  | { ok: false; reason: string; kind: 'empty' | 'file' | 'total' };

export interface CanAddArgs {
  /** Decoded bytes already attached (the running total, not counting this file). */
  existingBytes: number;
  fileSize: number;
  fileName: string;
  /** Overrides for composers with a tighter ceiling (classic composer). */
  maxFileBytes?: number;
  maxTotalBytes?: number;
}

/**
 * Decide at PICK time whether one more file fits. The wording is the exact
 * reason shown to the user, so the same numbers appear in the tooltip, the
 * indicator and every error.
 */
export function checkCanAdd(args: CanAddArgs): CanAddResult {
  const maxFile = args.maxFileBytes ?? MAIL_ATTACH_MAX_FILE_BYTES;
  const maxTotal = args.maxTotalBytes ?? MAIL_ATTACH_MAX_TOTAL_BYTES;
  const name = args.fileName || 'file';
  // A 0-byte "file" is a dragged folder or an unreadable placeholder, and Graph
  // rejects empty attachments - refuse it now instead of after the undo window.
  if (!(args.fileSize > 0)) {
    return { ok: false, kind: 'empty', reason: `${name} is empty (0 B) - nothing to attach` };
  }
  if (args.fileSize > maxFile) {
    return {
      ok: false,
      kind: 'file',
      reason: `${name} is ${formatBytes(args.fileSize)} - over the ${formatLimit(maxFile)} per-file limit`,
    };
  }
  const total = Math.max(0, args.existingBytes) + args.fileSize;
  if (total > maxTotal) {
    return {
      ok: false,
      kind: 'total',
      reason: `adding this would make the total ${formatBytes(total)} - over the ${formatLimit(maxTotal)} limit`,
    };
  }
  return { ok: true };
}

/**
 * Inline base64 (<= 150 KB, <= 400 KB total inline) or Storage upload. The
 * running inline total matters: ten 100 KB files must not all go inline.
 */
export function classify(file: { size: number }, inlineBytesSoFar: number = 0): 'inline' | 'storage' {
  if (file.size <= INLINE_MAX_FILE_BYTES && Math.max(0, inlineBytesSoFar) + file.size <= INLINE_MAX_TOTAL_BYTES) {
    return 'inline';
  }
  return 'storage';
}

// -- Composer attachment model -----------------------------------------------

export type AttachStatus = 'ready' | 'uploading' | 'error';

/** Local (in-component) attachment. Only a subset is ever persisted or sent. */
export interface ComposerAttachment {
  id: string;
  filename: string;
  contentType: string;
  size: number;
  /** base64, small files only */
  content?: string;
  /** Cloud Storage object path, large files only */
  storagePath?: string;
  status: AttachStatus;
  /** 0-100 while uploading */
  progress?: number;
  error?: string;
  /** Hydrated from a saved draft whose bytes the size guard had to drop. */
  trimmed?: boolean;
}

/** Contract B: the shape in draft docs AND send payloads. Exactly one of
 *  content | storagePath (metadata-only is a trimmed legacy draft item). */
export interface PersistedAttachment {
  filename: string;
  contentType?: string;
  size?: number;
  content?: string;
  storagePath?: string;
}

let idCounter = 0;
/** Local id / draft key: unique enough within a session, no dependency. */
export function newAttachmentId(): string {
  idCounter += 1;
  return `${Date.now().toString(36)}${idCounter.toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

/** Decoded byte length of a base64 string (without decoding it). */
export function base64DecodedBytes(b64: string): number {
  if (!b64) return 0;
  let pad = 0;
  if (b64.endsWith('==')) pad = 2;
  else if (b64.endsWith('=')) pad = 1;
  return Math.max(0, Math.floor((b64.length * 3) / 4) - pad);
}

/** Best-known decoded size of one attachment-ish object. */
export function attachmentBytes(a: { size?: number | null; content?: string | null }): number {
  if (typeof a.size === 'number' && Number.isFinite(a.size) && a.size > 0) return a.size;
  return a.content ? base64DecodedBytes(a.content) : 0;
}

export function sumAttachmentBytes(list: Array<{ size?: number | null; content?: string | null }>): number {
  return list.reduce((n, a) => n + attachmentBytes(a), 0);
}

/** Bytes currently held inline (for the classify() running total). */
export function sumInlineBytes(list: Array<{ size?: number | null; content?: string | null }>): number {
  return list.reduce((n, a) => n + (a.content ? attachmentBytes(a) : 0), 0);
}

/**
 * Persisted (draft doc) form of one local item, or null when it must not be
 * stored: an in-flight / failed upload has nothing durable to reference yet.
 * A trimmed legacy item keeps its METADATA so the draft still shows it.
 * Never includes undefined-valued keys (Firestore rejects them).
 */
export function toPersistedAttachment(a: ComposerAttachment): PersistedAttachment | null {
  const base: PersistedAttachment = { filename: a.filename };
  if (a.contentType) base.contentType = a.contentType;
  if (a.size > 0) base.size = a.size;
  if (a.status === 'ready' && a.storagePath) return { ...base, storagePath: a.storagePath };
  if (a.status === 'ready' && a.content) return { ...base, content: a.content };
  if (a.trimmed) return base;
  return null;
}

export function toPersistedAttachments(list: ComposerAttachment[]): PersistedAttachment[] {
  const out: PersistedAttachment[] = [];
  list.forEach((a) => {
    const p = toPersistedAttachment(a);
    if (p) out.push(p);
  });
  return out;
}

/** Send-payload form (contract B): ready items that actually carry bytes or a ref. */
export function toSendAttachments(list: ComposerAttachment[]): PersistedAttachment[] {
  return toPersistedAttachments(list.filter((a) => a.status === 'ready')).filter((p) => !!(p.content || p.storagePath));
}

/**
 * Message shown on a chip whose bytes were not saved with the draft. Metadata-
 * only items arise from MORE than one path (the draft size guard trimmed the
 * bytes, or duplicating a draft dropped the storage refs), so the wording names
 * no cause - only what the owner has to do.
 */
export const TRIMMED_ATTACHMENT_MESSAGE = 'not saved with this draft - remove it and attach it again';

/**
 * Hydrate attachments from a saved draft. Accepts BOTH refs (storagePath) and
 * legacy base64 items; an item with neither (bytes dropped by the draft size
 * guard, or refs dropped when a draft was duplicated) becomes an error chip so
 * it can't be sent empty.
 */
export function hydrateAttachments(raw: unknown, makeId: () => string = newAttachmentId): ComposerAttachment[] {
  if (!Array.isArray(raw)) return [];
  const out: ComposerAttachment[] = [];
  raw.forEach((r: any) => {
    const filename = typeof r?.filename === 'string' && r.filename ? r.filename : '';
    if (!filename) return;
    const contentType = typeof r.contentType === 'string' && r.contentType ? r.contentType : 'application/octet-stream';
    const storagePath = typeof r.storagePath === 'string' && r.storagePath ? r.storagePath : undefined;
    const content = typeof r.content === 'string' && r.content ? r.content : undefined;
    const size = attachmentBytes({ size: typeof r.size === 'number' ? r.size : undefined, content });
    if (storagePath) {
      out.push({ id: makeId(), filename, contentType, size, storagePath, status: 'ready' });
    } else if (content) {
      out.push({ id: makeId(), filename, contentType, size, content, status: 'ready' });
    } else {
      out.push({ id: makeId(), filename, contentType, size, status: 'error', trimmed: true, error: TRIMMED_ATTACHMENT_MESSAGE });
    }
  });
  return out;
}

/** Filename-only signature - compares local state to an incoming draft doc. */
export function attachmentNameSignature(list: Array<{ filename?: string }>): string {
  return list.map((a) => a?.filename).filter(Boolean).sort().join('|');
}

/** Stronger signature for "did the persisted attachments change?" (autosave). */
export function attachmentStateSignature(list: PersistedAttachment[]): string {
  return list
    .map((a) => `${a.filename}#${a.storagePath || (a.content ? `inline:${a.content.length}` : 'meta')}`)
    .sort()
    .join('|');
}

/** UTF-8 byte length of a string (Firestore counts bytes, not UTF-16 units). */
export function utf8Length(s: string): number {
  let n = 0;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c < 0x80) n += 1;
    else if (c < 0x800) n += 2;
    else if (c >= 0xd800 && c <= 0xdbff) { n += 4; i += 1; } // surrogate pair
    else n += 3;
  }
  return n;
}

/**
 * Draft-save guard (contract F): an autosave must NEVER hard-fail because of
 * attachment size. If the serialised attachments exceed `budget`, drop the
 * BYTES of inline items (largest first, until it fits) but keep their
 * metadata, and report `trimmed` so the doc can be flagged. Refs
 * (storagePath) and already-small items are untouched. Also strips
 * undefined-valued keys, which Firestore rejects outright.
 */
export function guardDraftAttachments(
  list: unknown,
  budget: number = DRAFT_ATTACHMENTS_MAX_BYTES,
): { attachments: any[]; trimmed: boolean } {
  if (!Array.isArray(list) || list.length === 0) return { attachments: [], trimmed: false };
  const items: any[] = list
    .filter((a) => a && typeof a === 'object')
    .map((a: any) => {
      const clean: any = {};
      Object.keys(a).forEach((k) => { if (a[k] !== undefined) clean[k] = a[k]; });
      return clean;
    });
  // Measured once (a legacy item can be megabytes of base64), then reduced by
  // each dropped payload - base64 is ASCII so its length IS its byte count.
  let total = utf8Length(JSON.stringify(items));
  if (total <= budget) return { attachments: items, trimmed: false };

  let trimmed = false;
  const order = items
    .map((it, i) => ({ i, bytes: typeof it.content === 'string' ? it.content.length : 0 }))
    .filter((x) => x.bytes > 0)
    .sort((a, b) => b.bytes - a.bytes);
  for (const { i, bytes } of order) {
    if (total <= budget) break;
    const it = items[i];
    // Keep the real size so the chip can still say how big the file was.
    if (!(typeof it.size === 'number' && it.size > 0)) it.size = base64DecodedBytes(it.content);
    delete it.content;
    total -= bytes;
    trimmed = true;
  }
  return { attachments: items, trimmed };
}

/** Why Send/Later must wait, or null when every attachment is ready. */
export function attachmentBlockReason(list: ComposerAttachment[]): string | null {
  const uploading = list.filter((a) => a.status === 'uploading');
  if (uploading.length > 0) {
    return `uploading ${uploading.length} file${uploading.length !== 1 ? 's' : ''} - send is available when it finishes`;
  }
  const failed = list.filter((a) => a.status === 'error');
  if (failed.length > 0) {
    const names = failed.slice(0, 2).map((a) => a.filename).join(', ') + (failed.length > 2 ? ` +${failed.length - 2} more` : '');
    // A draft item whose bytes were dropped can't be retried - only re-attached.
    if (failed.every((a) => a.trimmed)) {
      return `${names}: the file was not saved with this draft - remove it and attach it again before sending`;
    }
    return `${names} did not upload - retry or remove ${failed.length > 1 ? 'them' : 'it'} before sending`;
  }
  return null;
}

// -- cid: images in quoted HTML ----------------------------------------------

const CID_RE = /^\s*cid:/i;

/**
 * Remove every cid: reference from quoted HTML. <img src="cid:..."> elements
 * are dropped (keeping the surrounding text); any other element that points
 * an attribute / inline style at cid: has just that attribute neutralised.
 * cid: cannot resolve outside the original message, so a dangling one renders
 * a broken-image box for the recipient and in the editor.
 */
export function removeCidImages(html: string): string {
  if (!html || !/cid:/i.test(html)) return html;
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    Array.from(doc.querySelectorAll('img')).forEach((img) => {
      if (CID_RE.test(img.getAttribute('src') || '')) img.remove();
    });
    scrubOtherCidRefs(doc);
    return doc.body ? doc.body.innerHTML : html;
  } catch {
    return html
      .replace(/<img\b[^>]*\bsrc\s*=\s*["']?\s*cid:[^>]*>/gi, '')
      .replace(/(\s(?:src|href|background)\s*=\s*["'])\s*cid:[^"']*(["'])/gi, '$1$2');
  }
}

function scrubOtherCidRefs(doc: Document): void {
  Array.from(doc.querySelectorAll('*')).forEach((el) => {
    ['src', 'href', 'xlink:href', 'background', 'poster'].forEach((attr) => {
      if (CID_RE.test(el.getAttribute(attr) || '')) el.removeAttribute(attr);
    });
    const srcset = el.getAttribute('srcset') || '';
    if (/(^|[\s,])cid:/i.test(srcset)) el.removeAttribute('srcset');
    const style = el.getAttribute('style') || '';
    if (/url\(\s*['"]?\s*cid:/i.test(style)) {
      el.setAttribute('style', style.replace(/url\(\s*(['"]?)\s*cid:[^)]*\)/gi, 'none'));
    }
  });
}

/**
 * FORWARD: resolve cid: images to data: URLs (via the injected `resolve`, in
 * practice resolveInlineGraphImages) within a small budget, and leave NO
 * cid: reference behind. At most FORWARD_IMAGE_MAX_COUNT cid images are even
 * attempted; after resolution, any that failed or that would push the total
 * past FORWARD_IMAGE_MAX_BYTES are stripped. data: images that were already in
 * the source HTML are never touched.
 */
export async function resolveForwardCidImages(
  html: string,
  resolve: (html: string) => Promise<string>,
  opts: { maxImages?: number; maxBytes?: number } = {},
): Promise<string> {
  if (!html || !/cid:/i.test(html)) return html;
  const maxImages = opts.maxImages ?? FORWARD_IMAGE_MAX_COUNT;
  const maxBytes = opts.maxBytes ?? FORWARD_IMAGE_MAX_BYTES;
  let limited = html;
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const cidImgs = Array.from(doc.querySelectorAll('img')).filter((img) => CID_RE.test(img.getAttribute('src') || ''));
    cidImgs.slice(maxImages).forEach((img) => img.remove());
    limited = doc.body ? doc.body.innerHTML : html;
  } catch {
    return removeCidImages(html);
  }
  let resolved: string;
  try {
    resolved = await resolve(limited);
  } catch {
    return removeCidImages(limited);
  }
  try {
    const doc = new DOMParser().parseFromString(resolved, 'text/html');
    // The resolver marks every image it touched with data-wc-cid and only sets
    // a src on the ones it could fetch.
    let used = 0;
    Array.from(doc.querySelectorAll('img[data-wc-cid]')).forEach((img) => {
      const src = img.getAttribute('src') || '';
      const isData = /^data:image\//i.test(src);
      // base64 payload length * 3/4 ~ decoded bytes
      const bytes = isData ? Math.floor(((src.length - src.indexOf(',') - 1) * 3) / 4) : 0;
      if (!isData || used + bytes > maxBytes) {
        img.remove();
        return;
      }
      used += bytes;
      img.removeAttribute('data-wc-cid');
      img.removeAttribute('data-wc-alt');
    });
    scrubOtherCidRefs(doc);
    // Belt and braces: whatever cid img the resolver did not mark.
    Array.from(doc.querySelectorAll('img')).forEach((img) => {
      if (CID_RE.test(img.getAttribute('src') || '')) img.remove();
    });
    return doc.body ? doc.body.innerHTML : resolved;
  } catch {
    return removeCidImages(resolved);
  }
}
